import classNames from "classnames";
import dayjs from "dayjs";
import * as React from "react";
import { Legend } from "./Fieldset";
import { HelpText, HelpTextProps } from "./HelpText";
import { ValidationError } from "./ValidationError";

const CURRENT_DATE = dayjs();
const DAYS = Array.from(Array(31).keys()).map((v) => v + 1);
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface DateFieldMonthProps {
  id: string;
  name: string;
  label: string;
  optional?: boolean;
  helpText?: HelpTextProps["helpText"];
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (date: dayjs.Dayjs) => void;
  onBlur: (e: React.FocusEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
}

type DateFieldMonthForwardedRef = null | {
  fieldsetRef: HTMLFieldSetElement;
  dayInputRef: HTMLSelectElement;
  monthInputRef: HTMLSelectElement;
};

export const DateFieldMonth = React.forwardRef<
  DateFieldMonthForwardedRef,
  DateFieldMonthProps
>(
  (
    {
      id,
      name,
      value,
      error,
      touched,
      optional,
      helpText,
      onChange,
      onBlur,
      label,
      disabled = false,
    },
    ref
  ) => {
    const fieldSetRef = React.useRef<HTMLFieldSetElement>(null);
    const dayInputRef = React.useRef<HTMLSelectElement>(null);
    const monthInputRef = React.useRef<HTMLSelectElement>(null);

    React.useImperativeHandle(ref, () => ({
      fieldsetRef: fieldSetRef.current,
      dayInputRef: dayInputRef.current,
      monthInputRef: monthInputRef.current,
    }));

    const $onChange = React.useRef(onChange);
    const [date, setDate] = React.useState<dayjs.Dayjs | null>(() => {
      if (value) {
        return dayjs(value);
      }

      if (value === null) {
        return null;
      }

      return dayjs();
    });

    const { day, month, year, isoDate } = React.useMemo(() => {
      if (date === null) {
        return {
          day: 0,
          month: -1,
          year: 0,
          isoDate: "",
        };
      }

      return {
        day: date.date(),
        month: date.month(),
        year: date.year(),
        isoDate: date.toISOString(),
      };
    }, [date]);

    const handleDayChange = (e) => {
      const value = parseInt(e.target.value, 10);
      const _date = date || dayjs();

      setDate(value ? _date.set("date", value) : null);
    };

    const handleMonthChange = (e) => {
      const value = parseInt(e.target.value, 10);
      const _date = date || dayjs();

      // Note: January is month 0, so -1 is the value for no month selected
      setDate(value < 0 || value > 11 ? null : _date.set("month", value));
    };

    React.useEffect(() => {
      $onChange.current(date);
    }, [date, $onChange]);

    return (
      <fieldset
        ref={fieldSetRef}
        className="form-group"
        disabled={disabled}
        aria-required={!optional}
        aria-invalid={!!error && touched}
        aria-describedby={[
          helpText ? `${id}-help-text` : null,
          error && touched ? `${id}-error-message` : null,
        ]
          .filter((x) => x != null)
          .join(" ")}
      >
        <Legend>
          {label ?? "Expiry date"}{" "}
          {optional ? <small className="text-muted">(optional)</small> : "*"}
        </Legend>
        {helpText && <HelpText id={`${id}-help-text`} helpText={helpText} />}
        <div className="mx-n2 d-flex flex-wrap">
          <div className="px-2 col-4 col-sm-auto flex-grow-1 form-group">
            <label className="sr-only" htmlFor={`${id}__day`}>
              Day
            </label>
            <select
              ref={dayInputRef}
              id={`${id}__day`}
              className={classNames("form-control", {
                "is-invalid": !!error && touched,
              })}
              onChange={handleDayChange}
              onBlur={onBlur}
              value={day}
              aria-required={!optional}
              aria-invalid={error && touched}
              aria-describedby={[
                helpText ? `${id}-help-text` : null,
                error && touched ? `${id}-error-message` : null,
              ]
                .filter((x) => x != null)
                .join(" ")}
            >
              {optional && <option value={0}>-</option>}
              {DAYS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className="px-2 col-8 col-sm-auto flex-grow-1 form-group">
            <label className="sr-only" htmlFor={`${id}__month`}>
              Month
            </label>
            <select
              ref={monthInputRef}
              id={`${id}__month`}
              className={classNames("form-control", {
                "is-invalid": !!error && touched,
              })}
              onChange={handleMonthChange}
              onBlur={onBlur}
              value={month}
              aria-required={!optional}
              aria-invalid={error && touched}
              aria-describedby={[
                helpText ? `${id}-help-text` : null,
                error && touched ? `${id}-error-message` : null,
              ]
                .filter((x) => x != null)
                .join(" ")}
            >
              {optional && <option value={-1}>-</option>}
              {MONTHS.map((month, idx) => (
                <option key={month} value={idx}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <input type="hidden" name={`${id}__year`} value={year} />
          <input type="hidden" name={name} value={isoDate} />
        </div>
        {error && touched && (
          <ValidationError errorId={`${id}-error-message`} message={error} />
        )}
      </fieldset>
    );
  }
);
